import type { IllustrationKey, SlopeDifficulty, SlopeMode } from './slopeIllustrations/types';
import { getIllustrationKeyForMode } from './slopeIllustrations';

export type MobilityHints = {
  walk: string;
  car: string;
  tractor: string;
};

export function formatSlopeRounded(value: unknown): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(Math.ceil(Number(value)));
}

export function formatSlopePercent(value: unknown): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return `%${formatSlopeRounded(value)}`;
}

export function formatSlopePercentPrecise(value: unknown): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return `%${Number(value).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`;
}

export function formatSlopeMeters(value: unknown): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return `${formatSlopeRounded(value)} m`;
}

export function formatElevationRange(range: unknown): string {
  if (!Array.isArray(range) || range.length < 2) {
    return '—';
  }
  return `${formatSlopeMeters(range[0])} - ${formatSlopeMeters(range[1])}`;
}

/** 0–10 yeşil, 10–20 sarı, 20–30 turuncu, 30+ kırmızı */
export function getSlopeHeatColor(slope: unknown): string {
  const s = Number(slope);
  if (!Number.isFinite(s)) return '#94a3b8';
  let color = '#22c55e';
  if (s > 10) color = '#eab308';
  if (s > 20) color = '#f97316';
  if (s > 30) color = '#ef4444';
  return color;
}

export function getSlopeDescription(slope: unknown): string {
  if (slope == null || slope === '') return 'Veri yok';
  const n = Number(slope);
  if (!Number.isFinite(n) || n < 0) return 'Veri yok';
  if (n < 5) return 'Düz yürüyüş';
  if (n < 10) return 'Hafif yokuş';
  if (n < 20) return 'Belirgin yokuş';
  if (n < 30) return 'Çoğu insan zorlanır';
  if (n < 40) return 'Araç çıkamaz';
  return 'İş makinesi çıkamaz';
}

/** Özet / Parsel Eğimi: yürüyüş, araç, iş makinesi notları (tek kaynak) */
export function getMobilityHints(slope: unknown): MobilityHints {
  const s = Number(slope);
  if (!Number.isFinite(s)) {
    return { walk: '—', car: '—', tractor: '—' };
  }
  const walk =
    s < 10 ? 'Rahat' : s < 20 ? 'Hafif zorlanır' : s < 30 ? 'Yürüyüş zorlaşır' : 'Çok zor';
  const car =
    s < 15 ? 'Çıkabilir' : s < 28 ? 'Dikkat' : s < 38 ? 'Genelde çıkamaz' : 'Çıkamaz';
  const tractor = s < 22 ? 'Rahat' : s < 35 ? 'Dikkat' : 'Zorlanır / çıkamaz';
  return { walk, car, tractor };
}

export function getSlopeDifficulty(slopePercent: number): SlopeDifficulty {
  if (!Number.isFinite(slopePercent)) return 'easy';
  if (slopePercent < 10) return 'easy';
  if (slopePercent < 20) return 'struggle';
  return 'impossible';
}

export function getSlopeIllustrationKey(mode: SlopeMode, difficulty: SlopeDifficulty): IllustrationKey {
  return `${mode}_${difficulty}` as IllustrationKey;
}

export function inferStatusDifficulty(status: string): SlopeDifficulty | 'unknown' {
  const s = String(status || '').toLowerCase().trim();
  if (!s || s === '—') return 'unknown';
  if (s.includes('rahat') || s.includes('çıkabilir')) return 'easy';
  if (s.includes('dikkat') || s.includes('zorlan') || s.includes('zorlaş')) return 'struggle';
  return 'impossible';
}

export function resolveHeroIllustrationKey(
  slope: unknown,
  mode: SlopeMode = 'walking',
): IllustrationKey {
  return getIllustrationKeyForMode(slope as number | null | undefined, mode);
}
